
import React, { useState, useEffect, useRef } from 'react';

const Pong: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [score, setScore] = useState({ p1: 0, p2: 0 });
  const [active, setActive] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // HD DPI Scaling
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const cw = width, ch = height;
    let ball = { x: cw/2, y: ch/2, dx: 4, dy: 4, r: 10 };
    let p1 = { y: ch/2 - 40, h: 80, w: 12 };
    let p2 = { y: ch/2 - 40, h: 80, w: 12 };
    let p1Target = ch/2 - 40;

    const handleMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      p1Target = e.clientY - rect.top - p1.h/2;
    };
    const handleTouch = (e: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      p1Target = e.touches[0].clientY - rect.top - p1.h/2;
    };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('touchmove', handleTouch);

    const gameLoop = () => {
      if (!active) return;
      
      // Update
      p1.y += (p1Target - p1.y) * 0.15;
      p2.y += (ball.y - (p2.y + p2.h/2)) * 0.1; // Smarter AI
      
      ball.x += ball.dx;
      ball.y += ball.dy;

      if (ball.y <= ball.r || ball.y >= ch - ball.r) ball.dy *= -1;

      // Collisions with sharp logic
      if (ball.x <= p1.w + ball.r && ball.y > p1.y && ball.y < p1.y + p1.h) {
        ball.dx = Math.abs(ball.dx) * 1.05;
        ball.x = p1.w + ball.r;
      }
      if (ball.x >= cw - p2.w - ball.r && ball.y > p2.y && ball.y < p2.y + p2.h) {
        ball.dx = -Math.abs(ball.dx) * 1.05;
        ball.x = cw - p2.w - ball.r;
      }

      // Scoring
      if (ball.x < 0) { setScore(s => ({ ...s, p2: s.p2 + 1 })); reset(); }
      if (ball.x > cw) { setScore(s => ({ ...s, p1: s.p1 + 1 })); reset(); }

      function reset() {
        ball = { x: cw/2, y: ch/2, dx: 4 * (Math.random() > 0.5 ? 1 : -1), dy: 4 * (Math.random() > 0.5 ? 1 : -1), r: 10 };
      }

      // High Definition Drawing
      ctx.fillStyle = '#020617';
      ctx.fillRect(0, 0, cw, ch);

      // Center Line
      ctx.setLineDash([10, 10]);
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cw/2, 0);
      ctx.lineTo(cw/2, ch);
      ctx.stroke();
      ctx.setLineDash([]);

      // Neon Effects
      ctx.shadowBlur = 15;
      
      // Player 1
      ctx.shadowColor = '#3b82f6';
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath();
      ctx.roundRect(0, p1.y, p1.w, p1.h, 4);
      ctx.fill();

      // Player 2
      ctx.shadowColor = '#ef4444';
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.roundRect(cw - p2.w, p2.y, p2.w, p2.h, 4);
      ctx.fill();

      // Ball
      ctx.shadowColor = '#fff';
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI*2);
      ctx.fill();
      
      ctx.shadowBlur = 0; // Reset for performance
      
      requestAnimationFrame(gameLoop);
    };

    const animId = requestAnimationFrame(gameLoop);
    return () => {
      cancelAnimationFrame(animId);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('touchmove', handleTouch);
    };
  }, [active]);

  return (
    <div className="flex flex-col items-center game-container">
      <div className="mb-8 glass px-12 py-4 rounded-3xl flex gap-12 items-center">
        <div className="flex flex-col items-center">
          <span className="text-[10px] text-blue-400 font-bold tracking-widest uppercase">P1 (Local)</span>
          <span className="text-4xl font-black">{score.p1}</span>
        </div>
        <div className="h-8 w-px bg-slate-800"></div>
        <div className="flex flex-col items-center">
          <span className="text-[10px] text-red-400 font-bold tracking-widest uppercase">P2 (Bot)</span>
          <span className="text-4xl font-black">{score.p2}</span>
        </div>
      </div>

      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-red-600 rounded-2xl blur opacity-20 transition duration-1000"></div>
        <canvas 
          ref={canvasRef} 
          style={{ width: 'min(90vw, 600px)', height: '400px' }} 
          className="relative bg-slate-900/80 border border-slate-700/50 rounded-2xl cursor-none shadow-2xl" 
        />
      </div>

      {!active && (
        <button 
          onClick={() => setActive(true)} 
          className="mt-8 bg-white text-black hover:bg-slate-200 px-12 py-4 rounded-2xl font-black shadow-2xl shadow-white/10 transition-all hover:scale-105 active:scale-95 uppercase tracking-tighter"
        >
          Initialize Matrix
        </button>
      )}
    </div>
  );
};

export default Pong;
